import { useAllowFocus } from '@calendly/ui/hooks/use-allow-focus'
import { useLayoutEffect } from 'react'

import { getSchedulingFilterMenuPortalRoot } from 'components/cui_filter_menu/filter_menu_portal_root'
import { ensureFilterMenuPortalRoots } from 'components/cui_filter_menu/filter_menu_portals'
import { getMeetingsFilterMenuPortalRoot } from 'components/meetings/filter_modal/filter_menu_portal_root'

const scrubElementFocusBlockers = (element) => {
  if (!(element instanceof HTMLElement)) return

  if (element.hasAttribute('aria-hidden')) {
    element.removeAttribute('aria-hidden')
  }

  if (element.inert) {
    element.inert = false
  }
}

export const scrubFilterMenuPortalAriaHidden = (root) => {
  if (!(root instanceof HTMLElement)) return

  scrubElementFocusBlockers(root)
  root.querySelectorAll('[aria-hidden="true"]').forEach(scrubElementFocusBlockers)
  root.querySelectorAll('*').forEach((element) => {
    if (element instanceof HTMLElement && element.inert) {
      element.inert = false
    }
  })
}

const observeFilterMenuPortalAriaHidden = (roots, onChange) => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (
        mutation.type === 'attributes'
        && (mutation.attributeName === 'aria-hidden' || mutation.attributeName === 'inert')
        && mutation.target instanceof HTMLElement
        && roots.some((root) => root?.contains(mutation.target))
      ) {
        onChange()
      }

      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement && roots.some((root) => root?.contains(node))) {
          onChange()
        }
      })
    })
  })

  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['aria-hidden', 'inert'],
  })

  return observer
}

/**
 * Nested filter field menus portal outside the FilterMenu overlay. React Aria marks
 * those nodes aria-hidden, which blocks keyboard focus until they are allow-listed.
 */
export const useFilterMenuPortalFocusAllow = (isActive) => {
  const { assignFocusRef: assignMeetingsPortalFocus } = useAllowFocus()
  const { assignFocusRef: assignSchedulingPortalFocus } = useAllowFocus()

  useLayoutEffect(() => {
    if (!isActive) {
      assignMeetingsPortalFocus(null)
      assignSchedulingPortalFocus(null)
      return undefined
    }

    ensureFilterMenuPortalRoots()

    const meetingsPortal = getMeetingsFilterMenuPortalRoot()
    const schedulingPortal = getSchedulingFilterMenuPortalRoot()
    const portalRoots = [meetingsPortal, schedulingPortal].filter(Boolean)

    const sync = () => {
      portalRoots.forEach(scrubFilterMenuPortalAriaHidden)
    }

    assignMeetingsPortalFocus(meetingsPortal)
    assignSchedulingPortalFocus(schedulingPortal)
    sync()

    const observer = observeFilterMenuPortalAriaHidden(portalRoots, sync)

    return () => {
      assignMeetingsPortalFocus(null)
      assignSchedulingPortalFocus(null)
      observer.disconnect()
    }
  }, [assignMeetingsPortalFocus, assignSchedulingPortalFocus, isActive])
}
